const HABEN_PRESENT = ['habe', 'hast', 'hat', 'haben', 'habt', 'haben'];
const HABEN_PRETERITE = ['hatte', 'hattest', 'hatte', 'hatten', 'hattet', 'hatten'];
const SEIN_PRESENT = ['bin', 'bist', 'ist', 'sind', 'seid', 'sind'];
const SEIN_PRETERITE = ['war', 'warst', 'war', 'waren', 'wart', 'waren'];
const WERDEN_PRESENT = ['werde', 'wirst', 'wird', 'werden', 'werdet', 'werden'];
const SUBJUNCTIVE_ENDINGS = ['e', 'est', 'e', 'en', 'et', 'en'];
const PRONOUNS = new Set(['ich', 'du', 'er', 'sie', 'es', 'wir', 'ihr']);
function stemOf(infinitive) {
    return infinitive.endsWith('en') ? infinitive.slice(0, -2) : infinitive.slice(0, -1);
}
function needsLinkingE(stem) {
    return /(t|d|chn|ffn|gn|dn|tm|dm)$/.test(stem);
}
function regularPresent(stem) {
    const e = needsLinkingE(stem) ? 'e' : '';
    const du = /[sßxz]$/.test(stem) ? `${stem}t` : `${stem}${e}st`;
    return [`${stem}e`, du, `${stem}${e}t`, `${stem}en`, `${stem}${e}t`, `${stem}en`];
}
function strongPreterite(stem) {
    const duE = /[sßxzdt]$/.test(stem) ? 'e' : '';
    const ihrE = /[dt]$/.test(stem) ? 'e' : '';
    return [stem, `${stem}${duE}st`, stem, `${stem}en`, `${stem}${ihrE}t`, `${stem}en`];
}
function weakPreterite(stem) {
    const e = needsLinkingE(stem) ? 'e' : '';
    const base = `${stem}${e}t`;
    return [`${base}e`, `${base}est`, `${base}e`, `${base}en`, `${base}et`, `${base}en`];
}
function umlaut(stem) {
    return stem.replace(/au|a|o|u/, (m) => ({ au: 'äu', a: 'ä', o: 'ö', u: 'ü' })[m]);
}
function withParticle(forms, particle) {
    if (!particle)
        return forms;
    return forms.map((f) => (f ? `${f} ${particle}` : f));
}
/** Builds all nine tense tables for a German verb from its principal-part spec. */
export function conjugateVerb(spec) {
    const particle = spec.separable;
    const baseInfinitive = particle ? spec.infinitive.slice(particle.length).trim() : spec.infinitive;
    const stem = stemOf(baseInfinitive);
    const present = spec.present ??
        (() => {
            const forms = regularPresent(stem);
            if (spec.presentDu)
                forms[1] = spec.presentDu;
            if (spec.presentEr)
                forms[2] = spec.presentEr;
            return forms;
        })();
    const preterite = spec.preterite ??
        (spec.strongPreterite ? strongPreterite(spec.strongPreterite) : weakPreterite(stem));
    const subjunctive2 = spec.konjunktiv2Stem
        ? SUBJUNCTIVE_ENDINGS.map((e) => `${spec.konjunktiv2Stem}${e}`)
        : spec.strongPreterite
            ? SUBJUNCTIVE_ENDINGS.map((e) => `${umlaut(spec.strongPreterite)}${e}`)
            : preterite;
    const subjunctive1 = spec.konjunktiv1 ?? SUBJUNCTIVE_ENDINGS.map((e) => `${stem}${e}`);
    const duImperative = needsLinkingE(stem) ? `${stem}e` : `${stem}(e)`;
    const imperative = spec.imperative ?? {
        du: duImperative,
        wir: `${baseInfinitive} wir`,
        ihr: present[4],
        sie: `${baseInfinitive} Sie`,
    };
    const auxPresent = spec.auxiliary === 'sein' ? SEIN_PRESENT : HABEN_PRESENT;
    const auxPreterite = spec.auxiliary === 'sein' ? SEIN_PRETERITE : HABEN_PRETERITE;
    const tenses = {
        praesens: withParticle(present, particle),
        praeteritum: withParticle(preterite, particle),
        perfekt: auxPresent.map((a) => `${a} ${spec.participle}`),
        plusquamperfekt: auxPreterite.map((a) => `${a} ${spec.participle}`),
        futur1: WERDEN_PRESENT.map((w) => `${w} ${spec.infinitive}`),
        futur2: WERDEN_PRESENT.map((w) => `${w} ${spec.participle} ${spec.auxiliary}`),
        konjunktiv1: withParticle(subjunctive1, particle),
        konjunktiv2: withParticle(subjunctive2, particle),
        imperativ: withParticle(['', imperative.du, '', imperative.wir, imperative.ihr, imperative.sie], particle),
    };
    const er = tenses.praesens[2];
    const erPreterite = tenses.praeteritum[2];
    const principal_parts = `er ${er} · ${erPreterite} · ${spec.auxiliary === 'sein' ? 'ist' : 'hat'} ${spec.participle}`;
    return { auxiliary: spec.auxiliary, participle: spec.participle, principal_parts, tenses };
}
/**
 * Every single-word finite form of a verb (plus infinitive and participle). Compound tenses only
 * add helper verbs the user learns separately, so they are deliberately excluded.
 */
export function formsFromConjugation(infinitive, table, particle) {
    const out = new Set();
    const add = (token) => {
        const t = token.trim().toLowerCase();
        if (!t || PRONOUNS.has(t) || (particle && t === particle.toLowerCase()))
            return;
        out.add(t);
    };
    add(infinitive);
    add(table.participle);
    const simple = ['praesens', 'praeteritum', 'konjunktiv1', 'konjunktiv2', 'imperativ'];
    for (const key of simple) {
        for (const cell of table.tenses[key]) {
            for (const word of cell.split(/\s+/)) {
                const optional = word.match(/^(.+)\(e\)$/);
                if (optional) {
                    add(optional[1]);
                    add(`${optional[1]}e`);
                }
                else {
                    add(word);
                }
            }
        }
    }
    return Array.from(out);
}
